import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { useProducts } from '../ProductContext';
import { apiJson } from '../apiClient';
import { isFactoryRawMaterialCategory, factoryCategoryShort } from '../constants/factoryStockCategories';
import OverviewCommandPanel, {
  branchIdForApi,
  branchQueryForUser,
  readOverviewBranchFilter,
} from '../components/OverviewCommandPanel';
import { useLanguage } from '../i18n/LanguageContext';
import {
  canModerateBranch,
  canViewFinancials,
  firstDisplayNameFromUser,
  normalizeRole,
} from '../utils/userLocale';
import { tenantDisplayName } from '../utils/businessType';
import {
  dateKeyFromString,
  getLocalDateKey,
  isoWeekRangeForDateKey,
  isDateInRange,
} from '../utils/reportDates';
import { posTotalsFromSales } from '../utils/dashboardAnalytics';
import { factoryExpenseTotalAll } from '../constants/factoryExpenseCategories';
import {
  requestExpensesSummaryPrefs,
  requestReportDailyDate,
  requestReportWeeklyForDate,
} from '../utils/navigationFocus';
import { hasTenantModule } from '../utils/tenantModules';
import './FactoryDashboardPage.css';

function greetingForHour(t, date = new Date()) {
  const h = date.getHours();
  if (h < 12) return t('dash.greetingMorning');
  if (h < 17) return t('dash.greetingAfternoon');
  return t('dash.greetingEvening');
}

function formatTsh(value) {
  return Math.round(Number(value) || 0).toLocaleString();
}

function formatPieces(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return Math.round(n).toLocaleString();
}

function moneyValue(value) {
  return (
    <span className="factory-dash__money">
      <span className="factory-dash__money-currency">Tsh</span>
      <span className="factory-dash__money-amount">{formatTsh(value)}</span>
    </span>
  );
}

function filterSalesByBranch(list, branchId) {
  if (!branchId || !list?.length) return list || [];
  return list.filter((s) => String(s.branchId ?? '') === String(branchId));
}

function deliveryStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'delivered') return 'factory-dash__status--delivered';
  if (s === 'cancelled') return 'factory-dash__status--cancelled';
  return 'factory-dash__status--pending';
}

const FactoryDashboardPage = ({ user, onNavigate }) => {
  const { salesHistory, salesLoading } = useContext(SalesHistoryContext);
  const { products, inventoryLoading } = useProducts();
  const { t } = useLanguage();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const canViewExpenses = canModerateBranch(user?.role);
  const showFinancials = canViewFinancials(user?.role);
  const todayKey = getLocalDateKey();
  const firstName = firstDisplayNameFromUser(user);
  const factoryName = tenantDisplayName(user);
  const tenant = user?.tenant || {};
  const sub = user?.subscription || {};

  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [branchId, setBranchId] = useState(() => (isOwner ? readOverviewBranchFilter() : ''));
  const [expenseBucketsDay, setExpenseBucketsDay] = useState({ boss: 0, staff: 0, office: 0 });
  const [expenseBucketsWeek, setExpenseBucketsWeek] = useState({ boss: 0, staff: 0, office: 0 });
  const hasExpensesModule = hasTenantModule(user, 'expenses');

  const apiBranchId = branchIdForApi(user, branchId);

  const isoWeek = useMemo(() => isoWeekRangeForDateKey(todayKey), [todayKey]);

  const go = useCallback((key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  }, [onNavigate]);

  const goStat = useCallback(
    (card) => {
      if (typeof card.preNavigate === 'function') card.preNavigate();
      go(card.navKey);
    },
    [go]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = branchQueryForUser(user, branchId);
        const [sum, prod, del] = await Promise.all([
          apiJson(`/brick/summary${qs}`),
          apiJson(`/brick/production${qs}`),
          apiJson(`/brick/deliveries${qs}`),
        ]);
        if (cancelled) return;
        setSummary(sum);
        setBatches((prod.batches || []).slice(0, 5));
        setDeliveries((del.deliveries || []).slice(0, 5));
        setLoadErr('');
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || 'Could not load factory data.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, user]);

  useEffect(() => {
    if (!hasExpensesModule || !canViewExpenses) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const dayQs = new URLSearchParams({ window: 'day', date: todayKey });
        const weekQs = new URLSearchParams({ window: 'week', date: todayKey });
        const [dayData, weekData] = await Promise.all([
          apiJson(`/expenses/summary?${dayQs.toString()}`),
          apiJson(`/expenses/summary?${weekQs.toString()}`),
        ]);
        if (cancelled) return;
        setExpenseBucketsDay(dayData.buckets || { boss: 0, staff: 0, office: 0 });
        setExpenseBucketsWeek(weekData.buckets || { boss: 0, staff: 0, office: 0 });
      } catch {
        if (!cancelled) {
          setExpenseBucketsDay({ boss: 0, staff: 0, office: 0 });
          setExpenseBucketsWeek({ boss: 0, staff: 0, office: 0 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasExpensesModule, canViewExpenses, todayKey]);

  const salesToday = useMemo(() => {
    if (!salesHistory?.length) return [];
    const list = salesHistory.filter((s) => dateKeyFromString(s.date) === todayKey);
    return filterSalesByBranch(list, apiBranchId);
  }, [salesHistory, todayKey, apiBranchId]);

  const todayTotals = useMemo(() => posTotalsFromSales(salesToday), [salesToday]);

  const salesThisWeek = useMemo(() => {
    if (!salesHistory?.length) return [];
    const { fromDate, toDate } = isoWeek;
    const list = salesHistory.filter((s) =>
      isDateInRange(dateKeyFromString(s.date), fromDate, toDate)
    );
    return filterSalesByBranch(list, apiBranchId);
  }, [salesHistory, isoWeek, apiBranchId]);

  const weekTotals = useMemo(() => posTotalsFromSales(salesThisWeek), [salesThisWeek]);

  const todayExpenseTotal = useMemo(
    () => factoryExpenseTotalAll(expenseBucketsDay),
    [expenseBucketsDay]
  );
  const weekExpenseTotal = useMemo(
    () => factoryExpenseTotalAll(expenseBucketsWeek),
    [expenseBucketsWeek]
  );
  const todayNetProfit = useMemo(
    () => Math.round((todayTotals.profit - todayExpenseTotal + Number.EPSILON) * 100) / 100,
    [todayTotals.profit, todayExpenseTotal]
  );

  const weekNetProfit = useMemo(
    () => Math.round((weekTotals.profit - weekExpenseTotal + Number.EPSILON) * 100) / 100,
    [weekTotals.profit, weekExpenseTotal]
  );
  const producedTodayNet = Number(summary?.netProducedToday) || 0;
  const producedMonthGross = Number(summary?.producedThisMonth) || 0;
  const producedMonthNet = Number(summary?.netProducedMonth) ?? producedMonthGross;
  const wastageMonth = Number(summary?.wastageThisMonth) || 0;
  const trialActive = sub.status === 'trial' && sub.allowsAccess !== false;
  const daysLeft = sub.daysRemaining ?? sub.daysLeftInTrial ?? 0;
  const trialLengthDays = sub.trialLengthDays;

  const rawMaterials = useMemo(
    () => (products || []).filter((p) => isFactoryRawMaterialCategory(p.category)),
    [products]
  );

  const materialsReady = rawMaterials.length > 0;
  const brickTypesReady = Number(summary?.brickTypes) > 0;

  const workflowSteps = [
    {
      id: 'add',
      step: 1,
      title: 'Add materials',
      text: 'Register cement, sand, fuel, and other inputs — name and category only (no quantity or prices).',
      navKey: 'add-raw-material',
      done: materialsReady,
    },
    {
      id: 'suppliers',
      step: 2,
      title: 'Add suppliers',
      text: 'Register cement, sand, and other material suppliers before ordering.',
      navKey: 'factory-suppliers',
      done: false,
      disabled: !materialsReady,
    },
    {
      id: 'order',
      step: 3,
      title: 'Order materials',
      text: 'Select a saved supplier and catalog materials; enter purchase price (TZS) per unit.',
      navKey: 'orders-make',
      done: false,
      disabled: !materialsReady,
    },
    {
      id: 'receive',
      step: 4,
      title: 'Tracking & receive',
      text: isOwner
        ? 'Track order status and receive delivery — qty and selling price update stock.'
        : 'Owner tracks and receives the order — sets selling price and stock on receive.',
      navKey: 'orders-tracking',
      done: false,
      disabled: !materialsReady,
    },
    {
      id: 'stock',
      step: 5,
      title: 'View stock',
      text: 'On-hand quantities appear here after receive. Check raw materials and finished goods.',
      navKey: 'factory-stock-overview',
      done: false,
      disabled: !materialsReady,
    },
  ];

  const quickActions = [
    { key: 'add-raw-material', label: 'Add raw material', variant: 'amber' },
    { key: 'factory-suppliers', label: 'Add suppliers', variant: 'slate', disabled: !materialsReady },
    { key: 'orders-make', label: 'Order materials', variant: 'slate', disabled: !materialsReady },
    { key: 'orders-tracking', label: 'Tracking status', variant: 'cyan', disabled: !materialsReady },
    { key: 'factory-stock-overview', label: 'Stock', variant: 'indigo', disabled: !materialsReady },
    { key: 'factory-brick-types', label: 'Brick types', variant: 'orange', disabled: false },
    { key: 'brick-production', label: 'Batch', variant: 'orange', disabled: !brickTypesReady || !materialsReady },
    { key: 'pos-sale-products', label: 'Sale', variant: 'green' },
    { key: 'brick-pre-orders', label: 'Pre-order', variant: 'green' },
    { key: 'brick-deliveries', label: 'Delivery', variant: 'teal' },
    { key: 'brick-customer-docs', label: 'Quote / Invoice', variant: 'cyan' },
    ...(hasExpensesModule && canViewExpenses
      ? [{ key: 'expenses-summary', label: 'Expenses', variant: 'indigo' }]
      : []),
    ...(showFinancials
      ? [
          {
            key: 'report-daily',
            label: 'Daily',
            variant: 'rose',
            preNavigate: () => requestReportDailyDate(todayKey),
          },
          {
            key: 'report-weekly',
            label: 'Weekly',
            variant: 'slate',
            preNavigate: () => requestReportWeeklyForDate(todayKey),
          },
        ]
      : []),
  ];

  const statCards = [
    ...(showFinancials
      ? [
          {
            id: 'today-revenue',
            title: t('dash.revenueToday'),
            value: moneyValue(todayTotals.amount),
            caption: todayKey,
            hint: 'POS revenue for selected date.',
            navKey: 'report-daily',
            preNavigate: () => requestReportDailyDate(todayKey),
            isMoney: true,
          },
          {
            id: 'today-txns',
            title: t('dash.salesToday'),
            value: todayTotals.count,
            caption: todayKey,
            hint: 'Total POS transactions today.',
            navKey: 'report-daily',
            preNavigate: () => requestReportDailyDate(todayKey),
          },
          {
            id: 'today-profit',
            title: t('dash.profitToday'),
            value: moneyValue(todayTotals.profit),
            caption: todayKey,
            hint: 'POS margin for today.',
            navKey: 'pos-sale-history',
            isMoney: true,
          },
          ...(canViewExpenses
            ? [
                {
                  id: 'today-net',
                  title: t('dash.netToday'),
                  value: moneyValue(todayNetProfit),
                  caption: todayKey,
                  hint: 'Profit after all expenses for today.',
                  navKey: 'expenses-summary',
                  preNavigate: () =>
                    requestExpensesSummaryPrefs({ window: 'day', anchorDate: todayKey }),
                  isMoney: true,
                },
              ]
            : []),
          {
            id: 'week-revenue',
            title: t('dash.revenueWeek'),
            value: moneyValue(weekTotals.amount),
            caption: `${isoWeek.fromDate} ->  ${isoWeek.toDate}`,
            hint: 'POS revenue for current ISO week.',
            navKey: 'report-weekly',
            preNavigate: () => requestReportWeeklyForDate(todayKey),
            isMoney: true,
          },
          {
            id: 'week-profit',
            title: t('dash.profitWeek'),
            value: moneyValue(weekTotals.profit),
            caption: `${isoWeek.fromDate} ->  ${isoWeek.toDate}`,
            hint: 'POS margin for current ISO week.',
            navKey: 'report-weekly',
            preNavigate: () => requestReportWeeklyForDate(todayKey),
            isMoney: true,
          },
          ...(canViewExpenses
            ? [
                {
                  id: 'week-net',
                  title: t('dash.netWeek'),
                  value: moneyValue(weekNetProfit),
                  caption: `${isoWeek.fromDate} ->  ${isoWeek.toDate}`,
                  hint: 'Weekly profit after all expenses.',
                  navKey: 'expenses-summary',
                  preNavigate: () =>
                    requestExpensesSummaryPrefs({ window: 'week', anchorDate: todayKey }),
                  isMoney: true,
                },
              ]
            : []),
        ]
      : []),
    {
      id: 'produced-today',
      title: t('dash.bricksToday'),
      value: summary != null ? formatPieces(producedTodayNet) : '-',
      caption: todayKey,
      hint: 'Good bricks produced today (total minus wastage).',
      navKey: 'brick-production',
      preNavigate: () => requestReportDailyDate(todayKey),
    },
    {
      id: 'stock',
      title: t('dash.readyStock'),
      value: summary != null ? formatPieces(summary.finishedStockPieces) : '-',
      caption: 'pcs ready',
      hint: 'Matofali tayari kuuza — stock inayouzwa POS au delivery.',
      navKey: 'brick-catalog',
    },
    {
      id: 'produced',
      title: t('dash.bricksMonth'),
      value: summary != null ? formatPieces(producedMonthNet) : '-',
      caption: 'good pcs',
      hint: 'Bricks produced this month (minus wastage).',
      navKey: 'brick-production',
    },
    {
      id: 'wastage',
      title: t('dash.damagedMonth'),
      value: summary != null ? formatPieces(wastageMonth) : '-',
      caption: 'pcs damaged',
      hint: 'Idadi ya matofali yaliyoharibika mwezi huu.',
      navKey: 'damaged-products',
    },
    {
      id: 'deliveries',
      title: t('dash.deliveriesToday'),
      value:
        summary != null
          ? `${formatPieces(summary.deliveryPiecesToday ?? 0)} pcs`
          : '-',
      caption:
        summary != null
          ? `${summary.deliveriesToday ?? 0} delivered · pending ${summary.pendingDeliveries ?? 0}`
          : 'delivered today',
      hint: 'Bricks delivered today (completed trips only). Pending = all orders not yet dispatched.',
      navKey: 'brick-deliveries',
    },
  ];

  return (
    <div className="factory-dash page-bleed">
      <header className="factory-dash__hero">
        <div className="factory-dash__hero-inner">
          <div className="factory-dash__badge-row">
            <span className="factory-dash__badge">Brick factory</span>
            {tenant.businessTypeLabel ? (
              <span className="factory-dash__badge factory-dash__badge--plan">{tenant.businessTypeLabel}</span>
            ) : null}
          </div>
          <h1 className="factory-dash__greeting">
            {greetingForHour(t)}, {firstName}
          </h1>
          <p className="factory-dash__sub">
            <strong>{factoryName}</strong> - production, sales, deliveries, and key numbers in one clean dashboard.
          </p>
        </div>
      </header>

      {trialActive ? (
        <div className="factory-dash__trial" role="status">
          <strong>Free trial - full access</strong>
          {daysLeft > 0 ? (
            <span>
              {' '}
              - <strong>{daysLeft}</strong> days left
              {trialLengthDays ? ` (${trialLengthDays}-day trial)` : ''}. All menus and features are unlocked until your
              trial ends.
            </span>
          ) : (
            <span> - trial ending soon. Upgrade to keep access.</span>
          )}
          {hasTenantModule(user, 'subscription') ? (
            <button type="button" className="factory-dash__trial-link" onClick={() => go('subscription')}>
              View plans & upgrade
            </button>
          ) : null}
        </div>
      ) : null}

      {isOwner ? (
        <OverviewCommandPanel user={user} onNavigate={go} branchId={branchId} onBranchChange={setBranchId} />
      ) : null}

      {!trialActive && sub.allowsAccess === false ? (
        <div className="factory-dash__trial factory-dash__trial--warn" role="alert">
          Subscription is inactive.{' '}
          <button type="button" className="factory-dash__trial-link" onClick={() => go('subscription')}>
            Renew to continue
          </button>
        </div>
      ) : null}

      {loadErr ? <p className="factory-dash__err">{loadErr}</p> : null}

      {!brickTypesReady && !loadErr ? (
        <section className="factory-dash__prod-banner" aria-label="Production setup">
          <div>
            <h2 className="factory-dash__prod-banner-title">{t('factory.setupBrickTitle')}</h2>
            <p className="factory-dash__prod-banner-text">{t('factory.setupBrickText')}</p>
          </div>
          <button type="button" className="factory-dash__prod-banner-btn" onClick={() => go('factory-brick-types')}>
            {t('factory.setupBrickCta')}
          </button>
        </section>
      ) : null}

      <section className="factory-dash__workflow" aria-label="Materials workflow">
        <div className="factory-dash__section-head">
          <h2 className="factory-dash__section-title">Materials workflow</h2>
          <p className="factory-dash__section-sub">
            Add inputs first, order from your list, then receive — stock on hand updates on receive.
          </p>
        </div>
        <div className="factory-dash__workflow-grid">
          {workflowSteps.map((s) => (
            <article
              key={s.id}
              className={`factory-dash__workflow-step${s.done ? ' factory-dash__workflow-step--done' : ''}${s.disabled ? ' factory-dash__workflow-step--disabled' : ''}`}
            >
              <span className="factory-dash__workflow-num">{s.step}</span>
              <h3 className="factory-dash__workflow-title">{s.title}</h3>
              <p className="factory-dash__workflow-text">{s.text}</p>
              <button
                type="button"
                className="factory-dash__workflow-cta"
                disabled={s.disabled}
                onClick={() => go(s.navKey)}
              >
                {s.id === 'add' ? (materialsReady ? 'Add more' : 'Start here') : s.title}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="factory-dash__panel factory-dash__panel--wide factory-dash__materials-panel">
        <div className="factory-dash__section-head">
          <h2 className="factory-dash__section-title">Raw materials on hand</h2>
          <p className="factory-dash__section-sub">
            {materialsReady
              ? `${rawMaterials.length} material(s) tracked — quantities increase when orders are received.`
              : 'No materials yet. Add cement, sand, and other inputs to begin.'}
          </p>
        </div>
        {inventoryLoading ? (
          <p className="factory-dash__empty">Loading materials…</p>
        ) : !materialsReady ? (
          <>
            <p className="factory-dash__empty">
              Register your raw materials before placing supplier orders or recording production.
            </p>
            <button type="button" className="factory-dash__empty-cta" onClick={() => go('add-raw-material')}>
              + Add first raw material
            </button>
          </>
        ) : (
          <>
            <div className="factory-dash__table-wrap">
              <table className="factory-dash__table">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Category</th>
                    <th>On hand</th>
                    {showFinancials ? <th>Unit cost</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {rawMaterials.slice(0, 8).map((m) => (
                    <tr key={m.id}>
                      <td>{m.name}</td>
                      <td>{factoryCategoryShort(m.category)}</td>
                      <td>
                        <strong className={Number(m.quantity) <= 0 ? 'factory-dash__qty-zero' : ''}>
                          {formatPieces(m.quantity)}
                        </strong>
                      </td>
                      {showFinancials ? (
                        <td>{Number(m.purchasingPrice) > 0 ? moneyValue(m.purchasingPrice) : '—'}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="factory-dash__materials-actions">
              <button type="button" className="factory-dash__link" onClick={() => go('raw-materials-overview')}>
                Full materials view →
              </button>
              <button type="button" className="factory-dash__link" onClick={() => go('orders-make')}>
                Order materials →
              </button>
            </div>
          </>
        )}
      </section>

      <div className="factory-dash__cards">
        {statCards.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`factory-dash__card factory-dash__card--${c.id}${c.isMoney ? ' factory-dash__card--money' : ''}`}
            title={c.hint}
            onClick={() => goStat(c)}
          >
            <span className="factory-dash__card-title">{c.title}</span>
            {c.caption ? <span className="factory-dash__card-caption">{c.caption}</span> : null}
            <span className="factory-dash__card-value">{c.value}</span>
          </button>
        ))}
      </div>

      <section className="factory-dash__quick" aria-label="Quick actions">
        <div className="factory-dash__section-head">
          <h2 className="factory-dash__section-title">{t('dash.quickActions')}</h2>
        </div>
        <div className="factory-dash__quick-grid">
          {quickActions.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`factory-dash__quick-card factory-dash__quick-card--${a.variant}${a.disabled ? ' factory-dash__quick-card--disabled' : ''}`}
              disabled={a.disabled}
              title={a.disabled ? 'Add materials first' : undefined}
              onClick={() => {
                if (a.disabled) return;
                if (typeof a.preNavigate === 'function') a.preNavigate();
                go(a.navKey || a.key);
              }}
            >
              <span className="factory-dash__quick-label">{a.label}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="factory-dash__grid">
        <section className="factory-dash__panel">
          <div className="factory-dash__section-head">
            <h2 className="factory-dash__section-title">
              <span className="factory-dash__panel-icon" aria-hidden>
                
              </span>
              {t('dash.recentProduction')}
            </h2>
          </div>
          {batches.length === 0 ? (
            <>
              <p className="factory-dash__empty">No batches yet. Start tracking output and wastage for each run.</p>
              <button type="button" className="factory-dash__empty-cta" onClick={() => go('brick-production')}>
                + Record first batch
              </button>
            </>
          ) : (
            <div className="factory-dash__table-wrap">
              <table className="factory-dash__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Net pcs</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id}>
                      <td>{b.productionDate}</td>
                      <td>{b.brickTypeName}</td>
                      <td>
                        <strong>{b.netPieces}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button type="button" className="factory-dash__link" onClick={() => go('brick-production')}>
            View all production -> 
          </button>
        </section>

        <section className="factory-dash__panel">
          <div className="factory-dash__section-head">
            <h2 className="factory-dash__section-title">
              <span className="factory-dash__panel-icon" aria-hidden>
                
              </span>
              {t('dash.recentDeliveries')}
            </h2>
          </div>
          {deliveries.length === 0 ? (
            <>
              <p className="factory-dash__empty">No deliveries yet. Dispatch completed bricks to customers.</p>
              <button type="button" className="factory-dash__empty-cta" onClick={() => go('brick-deliveries')}>
                + New delivery
              </button>
            </>
          ) : (
            <div className="factory-dash__table-wrap">
              <table className="factory-dash__table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => (
                    <tr key={d.id}>
                      <td>{d.deliveryDate}</td>
                      <td>{d.customerName}</td>
                      <td>
                        <span className={`factory-dash__status ${deliveryStatusClass(d.status)}`}>
                          {d.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button type="button" className="factory-dash__link" onClick={() => go('brick-deliveries')}>
            View all deliveries -> 
          </button>
        </section>
      </div>

      {showFinancials ? (
        <section className="factory-dash__panel factory-dash__panel--wide">
          <div className="factory-dash__section-head">
            <h2 className="factory-dash__section-title">
              <span className="factory-dash__panel-icon" aria-hidden>
                
              </span>
              {t('dash.counterSales')}
            </h2>
            <p className="factory-dash__section-sub">{todayKey}</p>
          </div>
          <div className="factory-dash__table-wrap">
            <table className="factory-dash__table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Amount (TZS)</th>
                </tr>
              </thead>
              <tbody>
                {salesLoading && !salesToday.length ? (
                  <tr>
                    <td colSpan={4}>Loading...</td>
                  </tr>
                ) : salesToday.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <span className="factory-dash__empty" style={{ margin: 0 }}>
                        No counter sales today.
                      </span>
                      <button
                        type="button"
                        className="factory-dash__empty-cta"
                        style={{ marginTop: 8 }}
                        onClick={() => go('pos-sale-products')}
                      >
                        Open POS
                      </button>
                    </td>
                  </tr>
                ) : (
                  salesToday.slice(0, 6).map((s, i) => (
                    <tr key={s.id != null ? String(s.id) : `s-${i}`}>
                      <td>{s.date || '-'}</td>
                      <td>{s.product?.name || '-'}</td>
                      <td>{s.quantity ?? '-'}</td>
                      <td>
                        <strong>{s.amount != null ? formatTsh(s.amount) : '-'}</strong>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {salesToday.length > 0 ? (
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      <strong>Totals</strong>
                      <span className="factory-dash__table-totals-muted"> (matches Daily report)</span>
                    </td>
                    <td>{todayTotals.qty.toLocaleString()}</td>
                    <td>
                      <strong>{formatTsh(todayTotals.amount)}</strong>
                    </td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
          {isOwner ? (
            <button
              type="button"
              className="factory-dash__link"
              onClick={() => {
                requestReportDailyDate(todayKey);
                go('report-daily');
              }}
            >
              Daily report -> 
            </button>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default FactoryDashboardPage;
